import React, { useState, useMemo } from "react";
import { X } from "lucide-react";

// Default rates — editable inline; will be seeded from Settings once wired.
const DEFAULT_RATES = {
  dtf: { chest: 40, sleeve: 40, back_per_sqin: 5 },
  screen: { base_first_color: 30, per_extra_color: 15 },
  embroidery: { chest: 60, sleeve: 60, back: 85 },
};

/** Live calculator — outputs total customization cost per piece.
 *  Emits value via onChange(costPerPiece) whenever inputs change.
 */
export default function PricingCalculator({ initialMethod = "dtf", onChange, onClose }) {
  const [method, setMethod] = useState(initialMethod);
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [locations, setLocations] = useState({ chest: false, back: false, sleeve: false });
  const [backSqIn, setBackSqIn] = useState(0);   // DTF only
  const [colors, setColors] = useState(1);       // Screen only

  const cost = useMemo(() => {
    let total = 0;
    if (method === "dtf") {
      if (locations.chest) total += rates.dtf.chest;
      if (locations.sleeve) total += rates.dtf.sleeve;
      if (locations.back) total += (Number(backSqIn) || 0) * rates.dtf.back_per_sqin;
    } else if (method === "screen") {
      const anyLoc = locations.chest || locations.back || locations.sleeve;
      if (anyLoc) {
        const n = Math.max(1, Number(colors) || 1);
        total = rates.screen.base_first_color + (n - 1) * rates.screen.per_extra_color;
      }
    } else if (method === "embroidery") {
      if (locations.chest) total += rates.embroidery.chest;
      if (locations.sleeve) total += rates.embroidery.sleeve;
      if (locations.back) total += rates.embroidery.back;
    }
    return Math.round(total * 100) / 100;
  }, [method, locations, backSqIn, colors, rates]);

  React.useEffect(() => { onChange && onChange(cost, { method, locations, backSqIn, colors }); }, [cost]); // eslint-disable-line

  const Tab = ({ id, label }) => (
    <button type="button" onClick={()=>setMethod(id)}
      className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
        method===id ? "bg-[#242321] text-white" : "bg-[#F6F1E7] text-[#757169] hover:bg-[#EAE5DB]"
      }`}
      data-testid={`calc-method-${id}`}
      style={{fontFamily:"Manrope, sans-serif"}}>{label}</button>
  );

  const Loc = ({ id, label }) => (
    <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors ${
      locations[id] ? "border-[#C45A41] bg-[#FBEDE6]" : "border-[#EAE5DB] bg-white hover:bg-[#F6F1E7]"
    }`} data-testid={`calc-loc-${id}`}>
      <input type="checkbox" checked={locations[id]} onChange={e=>setLocations({...locations,[id]:e.target.checked})} className="accent-[#C45A41]"/>
      {label}
    </label>
  );

  const RateInput = ({ path, label }) => {
    const val = path.split(".").reduce((a,k)=>a?.[k], rates);
    return (
      <div><div className="text-[10px] text-[#757169] mb-0.5">{label}</div>
        <input type="number" step="0.5" className="input-field text-sm" value={val}
          onChange={e=>{
            const v = Number(e.target.value);
            const parts = path.split(".");
            setRates(r => {
              const next = JSON.parse(JSON.stringify(r));
              let cur = next; for (let i=0;i<parts.length-1;i++) cur = cur[parts[i]];
              cur[parts[parts.length-1]] = v; return next;
            });
          }} />
      </div>
    );
  };

  return (
    <div className="card-surface p-4 fade-up" data-testid="calc-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tiny">Customization cost calculator</div>
        {onClose && <button type="button" onClick={onClose} className="text-[#757169] hover:text-[#B83A3A]"><X size={14}/></button>}
      </div>

      <div className="flex gap-1.5 mb-4"><Tab id="dtf" label="DTF Print"/><Tab id="screen" label="Screen Print"/><Tab id="embroidery" label="Embroidery"/></div>

      <div className="label-tiny mb-1.5">Print locations</div>
      <div className="grid grid-cols-3 gap-2 mb-4"><Loc id="chest" label="Chest"/><Loc id="back" label="Back"/><Loc id="sleeve" label="Sleeve"/></div>

      {method === "dtf" && locations.back && (
        <div className="mb-4 p-3 bg-[#FBF8F1] rounded-md">
          <div className="text-[10px] text-[#757169] mb-1">Back print area (sq inches)</div>
          <input type="number" className="input-field text-sm" value={backSqIn} onChange={e=>setBackSqIn(Number(e.target.value))} data-testid="calc-sqin"/>
        </div>
      )}
      {method === "screen" && (
        <div className="mb-4 p-3 bg-[#FBF8F1] rounded-md">
          <div className="text-[10px] text-[#757169] mb-1">Number of ink colors in design</div>
          <input type="number" min="1" className="input-field text-sm" value={colors} onChange={e=>setColors(Number(e.target.value))} data-testid="calc-colors"/>
        </div>
      )}

      <details className="mb-4">
        <summary className="text-xs text-[#757169] cursor-pointer">Adjust rates for this order</summary>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {method === "dtf" && (<>
            <RateInput path="dtf.chest" label="Chest rate ₹"/>
            <RateInput path="dtf.sleeve" label="Sleeve rate ₹"/>
            <RateInput path="dtf.back_per_sqin" label="Back ₹/sq in"/>
          </>)}
          {method === "screen" && (<>
            <RateInput path="screen.base_first_color" label="Base (1st color)"/>
            <RateInput path="screen.per_extra_color" label="Per extra color"/>
          </>)}
          {method === "embroidery" && (<>
            <RateInput path="embroidery.chest" label="Chest rate ₹"/>
            <RateInput path="embroidery.sleeve" label="Sleeve rate ₹"/>
            <RateInput path="embroidery.back" label="Back rate ₹"/>
          </>)}
        </div>
      </details>

      <div className="flex items-center justify-between p-3 bg-[#DDEADE] rounded-md">
        <div className="text-sm">
          <div className="text-[#2E4A38] text-xs">Cost per piece</div>
          <div className="font-display font-bold text-2xl text-[#2E4A38]">₹{cost.toFixed(2)}</div>
        </div>
        <div className="text-xs text-[#2E4A38] text-right">Fills line-item.<br/>Still editable.</div>
      </div>
    </div>
  );
}
